import { useRef } from 'react';

import {
  PeerTypes,
  useShowCampaignById_new,
  useShowDonationItem,
  useShowStoreItem,
} from '@givebacks/api/store';
import { CheckoutButtonsProps } from '@givebacks/shop';
import { CauseCard } from 'core/Components';
import { Box, Modal, ModalProps, ScrollArea, Stack } from 'ui';

import { CampaignCard } from '../../../crowdfunding/components/Cards/CampaignCard/CampaignCard';
import { StoreItemOptionsForm } from '../forms/StoreItemOptionsForm/StoreItemOptionsForm';

type PendingCartFields = {
  credit_to: string[] | undefined;
  credit_write_in: string[] | undefined;
  peerType?: PeerTypes | null;
};

type StoreItemOptionsProps = {
  causeId?: string;
  storeItemId?: string;
  onBuyNow?: () => void;
  cartId?: string;
  pendingCartFields?: PendingCartFields;
  /** Should the add to cart Button even be available. Not mutateable */
  isAddToCartAvailable?: boolean;
  checkoutButtonsProps?: Pick<CheckoutButtonsProps, 'buyNowProps'>;
};
interface StoreItemOptionsModalProps extends ModalProps, StoreItemOptionsProps {}

const StoreItemOptions = ({
  causeId,
  storeItemId,
  onBuyNow,
  pendingCartFields,
  isAddToCartAvailable,
  checkoutButtonsProps,
}: StoreItemOptionsProps) => {
  const { data: storeItem } = useShowStoreItem({ uuid: storeItemId, causeId: causeId });

  if (!causeId || !storeItem || !storeItemId) return null;
  return (
    <StoreItemOptionsForm
      cartFields={pendingCartFields}
      causeId={causeId}
      storeItemId={storeItemId}
      onBuyNow={onBuyNow}
      itemAllowsRecurring={storeItem?.allow_recurring}
      itemAllowsPledging={storeItem?.__type == 'athon'}
      isAddToCartAvailable={isAddToCartAvailable}
      checkoutButtonsProps={checkoutButtonsProps}
    />
  );
};

const StoreItemOptionsModal = ({
  causeId,
  storeItemId,
  onBuyNow,
  opened,
  onClose,
  title,
  cartId,
  pendingCartFields,
  isAddToCartAvailable = true,
}: StoreItemOptionsModalProps) => {
  const isAddToCartAvailableRef = useRef(isAddToCartAvailable);

  const { data: campaign } = useShowCampaignById_new({
    campaignId: storeItemId,
    causeId,
  });

  const { data: donationItem } = useShowDonationItem({ storeId: causeId });

  const isCause = donationItem?.uuid === campaign?.uuid;

  return (
    <Modal
      title={title}
      opened={opened}
      onClose={onClose}
      scrollAreaComponent={ScrollArea.Autosize}
    >
      <Stack gap="lg">
        <Box>
          {isCause ? (
            <CauseCard cause_uuid={causeId} />
          ) : (
            <CampaignCard campaign_id={storeItemId} causeId={causeId} />
          )}
        </Box>

        <StoreItemOptions
          pendingCartFields={pendingCartFields}
          causeId={causeId}
          storeItemId={storeItemId}
          onBuyNow={onBuyNow}
          cartId={cartId}
          isAddToCartAvailable={isAddToCartAvailableRef.current}
        />
      </Stack>
    </Modal>
  );
};

export { StoreItemOptions, StoreItemOptionsModal };
